import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, X, Check, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DIVISIONS } from "@/data/divisions";
import { ADMIN } from "@/constants/testIds";

const EMPTY = { name: "", service: "", blurb: "", division_id: DIVISIONS[0].id, website_url: "" };

export default function AdminSubsidiaries() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterDiv, setFilterDiv] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSub, setNewSub] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subsidiaries");
      setSubs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return subs.filter((s) => {
      if (filterDiv !== "all" && s.division_id !== filterDiv) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.service.toLowerCase().includes(q) ||
        (s.website_url || "").toLowerCase().includes(q)
      );
    });
  }, [subs, query, filterDiv]);

  const create = async (e) => {
    e.preventDefault();
    if (!newSub.name || !newSub.service || !newSub.blurb || !newSub.division_id) {
      toast.error("Name, service, blurb and division are required.");
      return;
    }
    setCreating(true);
    try {
      const payload = { ...newSub, website_url: newSub.website_url || null };
      const { data } = await api.post("/admin/subsidiaries", payload);
      setSubs((xs) => [data, ...xs]);
      setNewSub(EMPTY);
      setShowAdd(false);
      toast.success("Subsidiary created.");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditDraft({ ...s, website_url: s.website_url || "" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };
  const saveEdit = async () => {
    try {
      const payload = { ...editDraft, website_url: editDraft.website_url || null };
      const { data } = await api.patch(`/admin/subsidiaries/${editingId}`, payload);
      setSubs((xs) => xs.map((x) => (x.id === editingId ? data : x)));
      toast.success("Subsidiary updated.");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this subsidiary? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/subsidiaries/${id}`);
      setSubs((xs) => xs.filter((x) => x.id !== id));
      toast.success("Subsidiary deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex-1 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search by name, service or URL..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-sub-search"
              className="w-full bg-[#0C1322] border border-white/10 pl-9 pr-3 py-2.5 text-sm focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none"
            />
          </div>
          <select
            value={filterDiv}
            onChange={(e) => setFilterDiv(e.target.value)}
            data-testid="admin-sub-filter-div"
            className="bg-[#0C1322] border border-white/10 px-3 py-2.5 text-sm focus:border-[#FF5722] outline-none"
          >
            <option value="all">All divisions</option>
            {DIVISIONS.map((d) => (
              <option key={d.id} value={d.id}>{d.code} · {d.name.replace("Billtech ", "")}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          data-testid="admin-sub-add-toggle"
          className="btn-primary text-xs"
        >
          {showAdd ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add Subsidiary</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="border border-[#FF5722] bg-[#FF5722]/5 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Entity name *">
            <input required value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} data-testid="admin-sub-new-name" className={inputCls} placeholder="e.g. Kevin Andrew Digital Twin Services" />
          </Field>
          <Field label="Division *">
            <select required value={newSub.division_id} onChange={(e) => setNewSub({ ...newSub, division_id: e.target.value })} data-testid="admin-sub-new-division" className={inputCls}>
              {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.name.replace("Billtech ", "")}</option>)}
            </select>
          </Field>
          <Field label="Service *">
            <input required value={newSub.service} onChange={(e) => setNewSub({ ...newSub, service: e.target.value })} data-testid="admin-sub-new-service" className={inputCls} placeholder="e.g. Digital Twin Modeling" />
          </Field>
          <Field label="Website URL">
            <input type="url" value={newSub.website_url} onChange={(e) => setNewSub({ ...newSub, website_url: e.target.value })} data-testid="admin-sub-new-website" className={inputCls} placeholder="https://..." />
          </Field>
          <Field label="Blurb (short description) *" full>
            <textarea required rows={2} value={newSub.blurb} onChange={(e) => setNewSub({ ...newSub, blurb: e.target.value })} data-testid="admin-sub-new-blurb" className={inputCls} />
          </Field>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={creating} data-testid="admin-sub-new-submit" className="btn-primary">
              {creating ? "Creating..." : "Create Subsidiary"}
            </button>
          </div>
        </form>
      )}

      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-3">
        {loading ? "Loading..." : `${filtered.length} of ${subs.length} entities`}
      </div>

      <div className="border border-white/10 bg-[#0C1322] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10">
            <tr className="text-left">
              {["Entity Name", "Service", "Division", "Website", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((s) =>
              editingId === s.id ? (
                <tr key={s.id} className="bg-[#FF5722]/5" data-testid={`admin-sub-edit-${s.id}`}>
                  <td className="px-4 py-3">
                    <input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} data-testid={`admin-sub-edit-name-${s.id}`} className={rowInputCls} />
                    <textarea rows={2} value={editDraft.blurb} onChange={(e) => setEditDraft({ ...editDraft, blurb: e.target.value })} data-testid={`admin-sub-edit-blurb-${s.id}`} className={`${rowInputCls} mt-1 text-xs`} />
                  </td>
                  <td className="px-4 py-3">
                    <input value={editDraft.service} onChange={(e) => setEditDraft({ ...editDraft, service: e.target.value })} data-testid={`admin-sub-edit-service-${s.id}`} className={rowInputCls} />
                  </td>
                  <td className="px-4 py-3">
                    <select value={editDraft.division_id} onChange={(e) => setEditDraft({ ...editDraft, division_id: e.target.value })} data-testid={`admin-sub-edit-division-${s.id}`} className={rowInputCls}>
                      {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.name.replace("Billtech ", "").split("&")[0].trim()}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input type="url" value={editDraft.website_url} onChange={(e) => setEditDraft({ ...editDraft, website_url: e.target.value })} data-testid={`admin-sub-edit-website-${s.id}`} placeholder="https://..." className={rowInputCls} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={saveEdit} data-testid={`admin-sub-save-${s.id}`} className="text-[#10B981] hover:text-white p-1" aria-label="Save"><Check size={14} /></button>
                      <button onClick={cancelEdit} data-testid={`admin-sub-cancel-${s.id}`} className="text-white/60 hover:text-white p-1" aria-label="Cancel"><X size={14} /></button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={ADMIN.subRow(s.id)}>
                  <td className="px-4 py-3">
                    <div className="font-display font-bold text-white">{s.name}</div>
                    <div className="text-[11px] text-white/50 line-clamp-1">{s.blurb}</div>
                  </td>
                  <td className="px-4 py-3 text-white/70">{s.service}</td>
                  <td className="px-4 py-3 text-white/60 text-[11px] font-mono uppercase tracking-widest">
                    {(DIVISIONS.find((d) => d.id === s.division_id)?.code) || "??"} · {s.division_id}
                  </td>
                  <td className="px-4 py-3 text-white/70 text-[11px]">
                    {s.website_url ? (
                      <a
                        href={s.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#00B0FF] hover:text-white"
                        data-testid={`admin-sub-link-${s.id}`}
                      >
                        {truncate(s.website_url, 32)} <ExternalLink size={11} />
                      </a>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => startEdit(s)} data-testid={ADMIN.subEditBtn(s.id)} className="text-white/60 hover:text-[#FF5722]" aria-label="Edit"><Pencil size={14} /></button>
                      <button onClick={() => remove(s.id)} data-testid={ADMIN.subDeleteBtn(s.id)} className="text-white/60 hover:text-[#EF4444]" aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              )
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-white/60">
                  No subsidiaries match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#060A14] border border-white/10 px-3 py-2 text-sm focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none";

const rowInputCls =
  "w-full bg-[#060A14] border border-white/10 px-2 py-1 text-xs focus:border-[#FF5722] outline-none";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}

function truncate(s, n) {
  return s.length > n ? s.slice(0, n) + "…" : s;
}
